import { Link } from "wouter";
import { SEOHead, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import { ChevronRight, BookOpen, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const PAGE_URL = "https://thepropertool.com/guides/";

const WEBPAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Guides for UK Builders | The Proper Tool",
  "description": "In-depth guides for UK builders: how to quote construction jobs accurately, how to run a building business, pricing benchmarks, and practical advice for sole traders and small firms.",
  "url": PAGE_URL,
  "publisher": {
    "@type": "Organization",
    "name": "The Proper Tool",
    "url": "https://thepropertool.com",
  },
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Guides", "item": PAGE_URL },
  ],
};

const GUIDES = [
  {
    href: "/guides/quoting-for-uk-builders/",
    title: "Quoting for UK Builders: The Complete Guide (2026)",
    summary:
      "Everything UK builders need to know about quoting jobs accurately: what quoting software does, how to price construction jobs, UK cost benchmarks, AI vs manual quoting, and the most common mistakes that cost builders money.",
    readTime: "18 min read",
    topics: ["Pricing", "Cost benchmarks", "AI quoting", "Common mistakes"],
  },
  {
    href: "/guides/running-a-building-business-uk/",
    title: "Running a Building Business in the UK: The Complete Guide (2026)",
    summary:
      "A practical guide to running a building business in the UK: business structure, insurance requirements, HMRC Making Tax Digital, managing subcontractors, quoting accurately, and keeping cash flow healthy.",
    readTime: "20 min read",
    topics: ["Business setup", "Insurance", "Tax & MTD", "Cash flow"],
  },
];

export default function GuidesIndexPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Guides for UK Builders | The Proper Tool"
        description="In-depth guides for UK builders: how to quote construction jobs accurately, run a building business, manage subcontractors, and price jobs with confidence."
        canonical="/guides"
        ogType="website"
        schema={[WEBPAGE_SCHEMA, BREADCRUMB_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="mb-12">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span>Guides</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Guides for UK Builders</h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Practical, in-depth guides written for UK builders, sole traders, and small construction firms. Covering quoting, pricing, business management, and more.
          </p>
        </div>

        <div className="space-y-6">
          {GUIDES.map((guide) => (
            <Link key={guide.href} href={guide.href}>
              <div className="group border border-border rounded-2xl p-6 sm:p-8 hover:border-primary/40 hover:bg-primary/5 transition-all cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                      {guide.readTime}
                    </p>
                    <h2 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors leading-snug">
                      {guide.title}
                    </h2>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                      {guide.summary}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {guide.topics.map((topic) => (
                        <span
                          key={topic}
                          className="inline-block text-xs font-medium bg-muted text-muted-foreground rounded-full px-3 py-1"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:gap-2 transition-all">
                      Read guide <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-16 rounded-2xl bg-muted/50 border border-border px-6 sm:px-8 py-8 text-center">
          <h2 className="text-xl font-bold mb-2">Ready to quote smarter?</h2>
          <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto">
            The Proper Tool generates accurate, itemised quotes from drawings, photos, or a plain-text description. Minutes, not hours.
          </p>
          <Button asChild>
            <Link href="/login">Start your free trial</Link>
          </Button>
        </div>
      </main>

      <SeoPageFooter />
    </div>
  );
}
